import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * Definition for a single filter field that can be synced to/from URL
 */
export interface FilterFieldDefinition {
    key: string;
    type: 'text' | 'dropdown' | 'date';
    defaultValue: any;
    serialize?: (value: any) => string | undefined;
    deserialize?: (value: string) => any;
    validate?: (value: any, context?: any) => boolean;
}

/**
 * Configuration options for useUrlFilters composable
 */
export interface UseUrlFiltersOptions {
    filterFields: FilterFieldDefinition[];
    onFiltersRestored?: (filters: Record<string, any>) => void;
    validationContext?: any;
}

/**
 * Composable for synchronizing filter state with URL query parameters
 *
 * Features:
 * - Syncs filters to URL on search/clear actions
 * - Restores filters from URL on page load
 * - Validates restored values against master data
 * - Prevents infinite loops with sync flags
 * - Omits default/null/empty values from URL
 *
 * @param options Configuration for filter synchronization
 * @returns Methods for syncing and restoring filters
 */
export function useUrlFilters(options: UseUrlFiltersOptions) {
    const route = useRoute();
    const router = useRouter();

    // Sync flags to prevent infinite loops
    const isSyncingToUrl = ref(false);
    const isSyncingFromUrl = ref(false);

    /**
     * Build query params from current filter values
     * Omits default/null/empty values for clean URLs
     */
    const buildQueryParams = (currentFilters: Record<string, any>): Record<string, string> => {
        const query: Record<string, string> = {};

        for (const fieldDef of options.filterFields) {
            const value = currentFilters[fieldDef.key];

            // Skip defaults/nulls/empty
            if (value === fieldDef.defaultValue || value == null || value === '') {
                continue;
            }

            // Serialize
            const serialized = fieldDef.serialize ? fieldDef.serialize(value) : String(value);

            if (serialized !== undefined && serialized !== '') {
                query[fieldDef.key] = serialized;
            }
        }

        return query;
    };

    /**
     * Sync current filter state to URL query parameters
     * Uses router.replace() to avoid creating history entries
     *
     * @param currentFilters Current filter values from component state
     */
    const syncFiltersToUrl = async (currentFilters: Record<string, any>) => {
        if (isSyncingFromUrl.value) return;

        isSyncingToUrl.value = true;

        try {
            const query = buildQueryParams(currentFilters);

            // Use replace to avoid creating history entries
            await router.replace({ query });
        } finally {
            isSyncingToUrl.value = false;
        }
    };

    /**
     * Restore filter state from URL query parameters
     * Validates values and applies defaults for invalid/missing params
     *
     * @returns Restored filter values (null if currently syncing to URL)
     */
    const restoreFiltersFromUrl = () => {
        if (isSyncingToUrl.value) return null;

        isSyncingFromUrl.value = true;

        try {
            const restoredFilters: Record<string, any> = {};

            for (const fieldDef of options.filterFields) {
                const queryValue = route.query[fieldDef.key];

                if (!queryValue || Array.isArray(queryValue)) {
                    // No value in URL, use default
                    restoredFilters[fieldDef.key] = fieldDef.defaultValue;
                    continue;
                }

                // Deserialize
                let deserializedValue = fieldDef.deserialize ? fieldDef.deserialize(queryValue) : queryValue;

                // Validate
                if (fieldDef.validate) {
                    const isValid = fieldDef.validate(deserializedValue, options.validationContext);
                    if (!isValid) {
                        deserializedValue = fieldDef.defaultValue;
                    }
                }

                restoredFilters[fieldDef.key] = deserializedValue;
            }

            // Notify parent component
            if (options.onFiltersRestored) {
                options.onFiltersRestored(restoredFilters);
            }

            return restoredFilters;
        } finally {
            isSyncingFromUrl.value = false;
        }
    };

    /**
     * Watch route query changes and restore filters
     * Handles browser back/forward navigation
     */
    const watchRouteChanges = () => {
        watch(
            () => route.query,
            () => {
                if (!isSyncingToUrl.value) {
                    restoreFiltersFromUrl();
                }
            },
            { deep: true }
        );
    };

    return {
        syncFiltersToUrl,
        restoreFiltersFromUrl,
        watchRouteChanges
    };
}
